import { TOKEN } from '@/constants';

/**
 * 获取服务端 Token
 *
 * 优先级：URL 参数 > Header > Cookie
 *
 * @returns Token 字符串或 undefined
 *
 * @description
 * 统一的服务端 token 获取函数，被以下模块使用：
 * - `axios.ts`：API 请求拦截器中获取 token
 * - `auth.ts`：判断免登录页面是否预加载数据
 *
 * @example
 * ```ts
 * // API 请求拦截器
 * const token = await getServerToken();
 * requestConfig.headers.Authorization = token ? `Bearer ${token}` : '';
 *
 * // 免登录页面鉴权判断
 * const serverToken = await getServerToken();
 * return !serverToken; // 无 token 则跳过调用
 * ```
 */
export const getServerToken = async (): Promise<string | undefined> => {
  try {
    const { cookies, headers } = await import('next/headers');
    const [xUrl, headerToken, cookieToken] = await Promise.all([
      headers().then(headers => headers.get('x-url')),
      headers().then(headers => headers.get(TOKEN)),
      cookies().then(cookies => cookies.get(TOKEN)?.value),
    ]);

    let urlToken = undefined;
    if (xUrl?.includes('token=')) {
      try {
        const url = new URL(xUrl);
        urlToken = url.searchParams.get(TOKEN);
      } catch {
        urlToken = '';
      }
    }

    return urlToken || headerToken || cookieToken || undefined;
  } catch {
    return undefined;
  }
};
